<script setup lang="ts">
/**
 * AuditLogSection — cursor-paginated workspace audit log.
 *
 * AI-triggered entries show BaseBadge variant="ai".
 * "Export" button is a stub anchor (CSV download deferred).
 */
import { ref, onMounted } from 'vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import BaseCard from '@/components/ui/BaseCard.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import BaseBadge from '@/components/ui/BaseBadge.vue';
import { fetchAuditLog } from '@/api/settings';
import type { AuditLogEntryDtoSettings } from '@/api/settings';

dayjs.extend(relativeTime);

const items = ref<AuditLogEntryDtoSettings[]>([]);
const nextCursor = ref<string | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);

async function load(cursor?: string) {
  isLoading.value = true;
  error.value = null;
  try {
    const page = await fetchAuditLog(cursor);
    if (cursor) {
      items.value = [...items.value, ...page.items];
    } else {
      items.value = page.items;
    }
    nextCursor.value = page.nextCursor ?? null;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load audit log.';
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  void load();
});

function formatTime(at: string): string {
  return dayjs(at).format('MMM D · h:mm A');
}
</script>

<template>
  <BaseCard id="audit" padding="none" data-testid="audit-section">
    <div class="p-4 sm:p-6 border-b border-border flex items-center justify-between gap-4">
      <div>
        <h3 class="text-base font-semibold text-fg">Audit log</h3>
        <p class="text-sm text-muted mt-0.5">Workspace events. Retained for 90 days.</p>
      </div>
      <!-- Export stub -->
      <button
        class="h-8 px-3 rounded-md border border-border bg-surface text-sm font-medium text-fg hover:bg-canvas-soft transition-colors"
        data-testid="audit-export-btn"
        title="CSV export coming soon"
      >
        Export
      </button>
    </div>

    <div class="p-4 sm:p-6">
      <div v-if="isLoading && items.length === 0" aria-busy="true" class="space-y-3">
        <div v-for="n in 4" :key="n" class="flex gap-3">
          <div class="w-2 h-2 mt-1.5 rounded-full bg-gray-200 animate-pulse shrink-0" />
          <div class="flex-1 space-y-1.5">
            <div class="h-3 bg-gray-200 rounded animate-pulse w-1/3" />
            <div class="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
          </div>
        </div>
      </div>

      <div v-else-if="error" class="text-sm text-red-600" role="alert">{{ error }}</div>

      <div v-else-if="items.length === 0" class="text-sm text-muted py-4 text-center">
        No audit log entries yet.
      </div>

      <ol v-else class="relative border-l border-border ml-2 space-y-0" data-testid="audit-timeline">
        <li
          v-for="item in items"
          :key="item.id"
          class="pb-5 pl-6 last:pb-0 relative"
        >
          <!-- Timeline dot -->
          <span class="absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 border-border bg-surface" />

          <div class="flex items-start gap-2 flex-wrap">
            <span class="text-xs font-mono text-muted tabular-nums mt-0.5 whitespace-nowrap">
              {{ formatTime(item.at) }}
            </span>

            <!-- AI badge for AI-triggered entries -->
            <BaseBadge
              v-if="item.actorIsAi"
              variant="ai"
              :no-dot="false"
              class="mt-0.5"
            >
              AI
            </BaseBadge>
          </div>

          <p class="text-sm text-fg mt-0.5">
            <strong>{{ item.actorName }}</strong> — {{ item.eventKind.replace(/_/g, ' ') }}
          </p>
          <p v-if="item.body" class="text-xs text-muted mt-0.5">{{ item.body }}</p>
        </li>
      </ol>

      <!-- Load more -->
      <div v-if="nextCursor" class="mt-4 flex justify-center">
        <BaseButton
          variant="secondary"
          size="sm"
          :loading="isLoading"
          data-testid="audit-load-more-btn"
          @click="() => void load(nextCursor ?? undefined)"
        >
          Load more
        </BaseButton>
      </div>
    </div>
  </BaseCard>
</template>
